import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, Radio } from "lucide-react";
import { scrollToSection } from "@/App";

const PORTRAIT = "https://static.wixstatic.com/media/c4f50b_e8d567251dfd436e8c187911c0bb521d~mv2.jpg/v1/crop/x_0,y_168,w_960,h_943/fill/w_600,h_590,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Vikram.jpg";

const LINES = ["Engineering", "S/4HANA Financial", "Mastery."];

const STATS = [
  { label: "SAP Professionals Trained", value: "12,000+" },
  { label: "Enterprise Transformations", value: "100+" },
  { label: "Certification Pass Rate", value: "98%" },
  { label: "Years in SAP Finance", value: "15+" },
];

const EASE = [0.22, 1, 0.36, 1];

const useCountdown = (target) => {
  const [left, setLeft] = useState({ d: 0, h: 0, m: 0 });
  useEffect(() => {
    const tick = () => {
      const diff = Math.max(0, new Date(target) - new Date());
      setLeft({
        d: Math.floor(diff / 86400000),
        h: Math.floor((diff / 3600000) % 24),
        m: Math.floor((diff / 60000) % 60),
      });
    };
    tick();
    const t = setInterval(tick, 30000);
    return () => clearInterval(t);
  }, [target]);
  return left;
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const { d, h, m } = useCountdown("2026-11-22T08:00:00+05:30");

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 hero-grid-bg pointer-events-none -z-10" />
      <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-amber-500/10 blur-[140px] pointer-events-none" />
      <div className="absolute top-1/3 -left-52 w-[480px] h-[480px] rounded-full bg-sky-500/[0.07] blur-[140px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto w-full px-6 lg:px-10 pt-36 pb-10 grid lg:grid-cols-12 gap-12 items-end">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.35 }}
            className="font-mono2 text-xs sm:text-sm uppercase tracking-[0.25em] text-amber-600 mb-6"
            data-testid="hero-chapter-label"
          >
            The Transformation Leader — Vikram Fotani
          </motion.p>

          <h1 className="font-display text-4xl sm:text-6xl lg:text-6xl xl:text-7xl font-extrabold tracking-tight leading-[1.03]" data-testid="hero-heading">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, ease: EASE, delay: 0.45 + i * 0.16 }}
                >
                  {i === 1 ? (
                    <>
                      <span className="text-amber-600">S/4HANA</span> Financial
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 1.15 }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
            data-testid="hero-subheadline"
          >
            SAP S/4HANA Transformation Leader & executive trainer. I help CFOs transform the office of finance — and help professionals build elite SAP careers through live cohorts and self-paced programs.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 1.3 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-explore-courses"
              onClick={() => scrollToSection("courses")}
              className="group flex items-center gap-2 bg-amber-400 text-slate-900 font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-amber-500 transition-colors duration-300"
            >
              Explore Course Catalog
              <ArrowDown size={16} className="group-hover:translate-y-0.5 transition-transform duration-300" />
            </button>
            <button
              data-testid="hero-cta-consulting"
              onClick={() => scrollToSection("consulting")}
              className="group flex items-center gap-2 border border-slate-300 text-slate-800 font-medium text-sm px-7 py-3.5 rounded-full hover:border-amber-400/60 hover:text-amber-700 transition-colors duration-300"
            >
              Enterprise Advisory
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.7 }}
          className="lg:col-span-5 relative"
        >
          <motion.div style={{ y: imgY }} className="relative">
            <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-tr from-amber-500/25 via-transparent to-sky-500/15 blur-xl" />
            <div className="relative rounded-[1.75rem] overflow-hidden border border-slate-200 bg-[#F8FAFC]">
              <img
                src={PORTRAIT}
                alt="Vikram Fotani — SAP S/4HANA Finance Principal"
                className="w-full aspect-[4/4.2] object-cover object-top"
                data-testid="hero-portrait"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#ffffff]/85 via-transparent to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-5">
                <div className="rounded-2xl border border-slate-200 bg-[#ffffff]/70 backdrop-blur-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
                    </span>
                    <span className="font-mono2 text-[10px] tracking-[0.2em] uppercase text-emerald-600 flex items-center gap-1">
                      <Radio size={10} /> Next Live FI Cohort — 22 Nov 2026
                    </span>
                  </div>
                  <div className="flex items-end justify-between">
                    <div className="font-mono2 text-2xl font-bold text-slate-900" data-testid="hero-cohort-countdown">
                      {d}<span className="text-xs text-slate-500 font-normal">d</span> : {String(h).padStart(2, "0")}<span className="text-xs text-slate-500 font-normal">h</span> : {String(m).padStart(2, "0")}<span className="text-xs text-slate-500 font-normal">m</span>
                    </div>
                    <div className="text-right">
                      <div className="font-mono2 text-lg font-bold text-amber-600">$400</div>
                      <div className="font-mono2 text-[10px] text-slate-500 line-through">$650</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      <div className="relative border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE, delay: 1.5 + i * 0.1 }}
              className="py-7 px-2 lg:px-6 border-l border-slate-200 first:border-l-0"
              data-testid={`hero-stat-${i}`}
            >
              <div className="font-mono2 text-3xl sm:text-4xl font-extrabold tracking-tight text-amber-600">{s.value}</div>
              <div className="mt-1 text-xs sm:text-sm text-slate-500">{s.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
